// Click-to-call, Phase 1: the one "Call" action, used in the Communications conversation header (pill and phone ⋯ menu)
// and on CRM client cards. Pressing it opens the "Call This Client" pop-up (a centred card, on phones too):
//   Line Selector → every company voice line the person may use, plus "My personal line — shows my own number" when a
//   number for the client is known. A company line: Make Call rings the staff member's own phone first with the company
//   line as caller ID; they answer and press 1 and the client is called from the company line, so the client sees the
//   company number, never their personal one. The personal line: Make Call dials straight from this device (the old
//   "Call from this device instead" tel: link, unchanged; no server call).
//
// One confirm = one one-time key, so a double click or a retry after a network error/timeout can never ring twice. When the
// server definitely refused (it answered with its own error, e.g. the line has no voice credentials or Twilio said no), the
// old key is spent: a fresh one is minted so "Make Call" really tries again instead of replaying the failure.
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import Modal3D from '../ui/Modal3D';
import Button3D from '../ui/Button3D';
import { SupportWriteGuard } from '../../context/SupportSessionContext';
import { formatPhone } from '../../lib/phone';
import { newCallKey, placeOutboundCall, prepareOutboundCall } from '../../lib/outboundCall';
import { D2_INPUT } from '../settings/signatures/d2Fields';

function lineLabel(line) {
    if (!line) return '';
    const number = formatPhone(line.phone) || line.phone;
    return line.label ? `${line.label} · ${number}` : number;
}

// The Line Selector value for "My personal line" (company line ids are database ids, never this).
export const PERSONAL_LINE = 'personal-device';
export const PERSONAL_LINE_LABEL = 'My personal line — shows my own number';
export const COMPANY_LINE_HELP = 'Your phone will ring first. The client sees the company line. Answer your phone and press 1.';
export const PERSONAL_LINE_HELP = 'Calls the client straight from this device. The client sees your own number.';
export const CALL_BUTTON_TIP = 'Call this client';

/**
 * Where focus goes when the pop-up closes: the Call button itself when it can be seen; when it lives inside the closed (hidden)
 * phone ⋯ menu, that menu's ⋯ button — the control the person actually sees and pressed to get here.
 */
export function callFocusTarget(button) {
    if (!button || !button.isConnected) return null;
    const hidden = button.closest('[hidden], [aria-hidden="true"], [inert]');
    if (!hidden && !button.disabled) return button;
    return button.closest('[data-thread-actions]')?.querySelector('[data-thread-actions-trigger]') || null;
}

export default function ClickToCallButton({
    client = null,
    sessionId = null,
    accountId = null,
    clientPhone = '',
    fallbackTel = '',
    label = 'Call',
    className = 'ia-btn3d ia-comms-pill',
    menuItem = false,
    disabled = false,
    disabledReason = '',
}) {
    const [open, setOpen] = useState(false);
    const [details, setDetails] = useState(null);
    const [lineId, setLineId] = useState('');
    const [phase, setPhase] = useState('idle'); // idle | loading | ready | calling | ringing
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const keyRef = useRef('');
    const cancelRef = useRef(null);
    const deviceCallRef = useRef(null);
    const buttonRef = useRef(null);
    const helpId = `c2c-help-${useId().replace(/:/g, '')}`;
    const alive = useRef(true);
    useEffect(() => () => { alive.current = false; }, []);

    const target = sessionId ? { sessionId } : { accountId, clientPhone };
    // Loaded only when someone actually calls, so showing a CRM card never needs the database client.
    const db = useCallback(async () => client || (await import('../../config/supabase')).supabaseClient, [client]);
    const usable = Boolean(sessionId || (accountId && clientPhone));

    const start = useCallback(async (event) => {
        // In the phone ⋯ menu the press must reach the menu so it closes behind the pop-up; elsewhere (a CRM card) it must not.
        if (!menuItem) event?.stopPropagation?.();
        if (disabled || !usable) return;
        keyRef.current = newCallKey();
        setOpen(true);
        setDetails(null);
        setError('');
        setNotice('');
        setLineId('');
        setPhase('loading');
        try {
            const prepared = await prepareOutboundCall(await db(), sessionId ? { sessionId } : { accountId, clientPhone });
            if (!alive.current) return;
            setDetails(prepared);
            setLineId(prepared.lineId || prepared.lines?.[0]?.id || (fallbackTel ? PERSONAL_LINE : ''));
            setPhase('ready');
        } catch (failure) {
            if (!alive.current) return;
            setError(failure.message || 'The call could not be prepared.');
            // The company line can't be used, but the person can still call from their own phone (as the old link allowed).
            if (fallbackTel) setLineId(PERSONAL_LINE);
            setPhase('idle');
        }
    }, [accountId, clientPhone, db, disabled, fallbackTel, menuItem, sessionId, usable]);

    const close = useCallback(() => {
        setOpen(false);
        setPhase('idle');
    }, []);

    const ring = useCallback(async () => {
        if (phase !== 'ready' || lineId === PERSONAL_LINE) return;
        setPhase('calling');
        setError('');
        try {
            const result = await placeOutboundCall(await db(), target, { numberId: lineId, idempotencyKey: keyRef.current });
            if (!alive.current) return;
            setNotice(result.message || 'Your phone is ringing. Answer it and press 1 to connect.');
            setPhase('ringing');
        } catch (failure) {
            if (failure?.definite) keyRef.current = newCallKey();
            if (!alive.current) return;
            setError(failure.message || 'The call could not be started.');
            setPhase('ready');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [db, lineId, phase, sessionId, accountId, clientPhone]);

    const lines = Array.isArray(details?.lines) ? details.lines : [];
    // A single company line arrives as linePhone only on older servers; it still belongs in the selector.
    const companyLines = lines.length ? lines : (details?.lineId ? [{ id: details.lineId, phone: details.linePhone }] : []);
    const personal = lineId === PERSONAL_LINE;
    const choices = [
        ...companyLines.map((line) => ({ value: line.id, label: lineLabel(line) })),
        ...(fallbackTel ? [{ value: PERSONAL_LINE, label: PERSONAL_LINE_LABEL }] : []),
    ];
    const showSelector = phase !== 'ringing' && phase !== 'loading' && choices.length > 0;
    const canCall = personal ? Boolean(fallbackTel) && phase !== 'calling' : phase === 'ready' && Boolean(lineId);

    const dialPersonal = () => {
        // Exactly the old "Call from this device instead" link: the device's own dialer, no server call.
        deviceCallRef.current?.click();
        close();
    };

    const dialog = open && (
        <Modal3D
            title="Call This Client"
            size="small"
            portal
            phoneLayout="card"
            closeOnScrim
            onClose={close}
            initialFocusRef={cancelRef}
            restoreFocus={() => callFocusTarget(buttonRef.current)?.focus()}
            testId="click-to-call-confirm"
            actions={phase === 'ringing' ? (
                <Button3D onClick={close} data-c2c-action="done">Done</Button3D>
            ) : (
                <>
                    <Button3D ref={cancelRef} onClick={close} data-c2c-action="cancel">Cancel</Button3D>
                    <Button3D
                        variant="go"
                        onClick={personal ? dialPersonal : ring}
                        disabled={!canCall}
                        aria-busy={phase === 'calling' ? 'true' : undefined}
                        data-c2c-action="ring"
                    >
                        {phase === 'calling' && !personal ? 'Ringing your phone…' : 'Make Call'}
                    </Button3D>
                </>
            )}
        >
            <div className="space-y-3 text-sm text-white" style={{ fontFamily: "'Exo 2', sans-serif" }}>
                {phase === 'loading' && <p aria-busy="true">Checking your phone and the company line…</p>}
                {showSelector && (
                    <label className="flex flex-col gap-1 font-bold" data-c2c-row="line-label">
                        <span data-c2c-line-label="true">Line Selector</span>
                        <select
                            aria-label="Line Selector"
                            className={`${D2_INPUT} min-h-[44px] w-full`}
                            value={lineId}
                            disabled={phase === 'calling'}
                            onChange={(event) => setLineId(event.target.value)}
                            aria-describedby={helpId}
                            data-c2c-row="line-select"
                        >
                            {choices.map((choice) => <option key={choice.value} value={choice.value}>{choice.label}</option>)}
                        </select>
                    </label>
                )}
                {showSelector && (
                    <p id={helpId} data-c2c-row="help" data-c2c-help={personal ? 'personal' : 'company'}>
                        {personal ? PERSONAL_LINE_HELP : COMPANY_LINE_HELP}
                    </p>
                )}
                {details?.recording && !personal && phase !== 'ringing' && (
                    <p data-c2c-recording="true">This company records calls. The client hears “This call may be recorded.” before connecting.</p>
                )}
                {notice && <p role="status" aria-live="polite" className="font-semibold" data-c2c-notice="true">{notice}</p>}
                {error && <p role="alert" className="rounded-lg border border-red-400 bg-red-500/15 p-2 font-semibold" data-c2c-error="true">{error}</p>}
                {fallbackTel && (
                    // The device dialer behind "My personal line" (never shown; Make Call presses it).
                    <a ref={deviceCallRef} href={`tel:${fallbackTel}`} hidden aria-hidden="true" tabIndex={-1} data-c2c-device-call="true">Call from this device</a>
                )}
            </div>
        </Modal3D>
    );

    return (
        // Clicks inside the confirm bubble through React to here; stop them so a CRM card never opens or collapses underneath.
        // In the phone ⋯ menu they are let through, so the menu closes the way every other menu item closes it.
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <span className="contents" onClick={menuItem ? undefined : (event) => event.stopPropagation()} data-click-to-call="true">
            <SupportWriteGuard mode="disable" tooltip="Calling is disabled in a read-only support session">
                <button
                    ref={buttonRef}
                    type="button"
                    className={className}
                    role={menuItem ? 'menuitem' : undefined}
                    onClick={start}
                    disabled={disabled || !usable}
                    title={disabled && disabledReason ? disabledReason : CALL_BUTTON_TIP}
                    data-testid="click-to-call-button"
                    data-thread-menu-item={menuItem ? 'call' : undefined}
                >
                    {label}
                </button>
            </SupportWriteGuard>
            {dialog}
        </span>
    );
}
